import { Box, Flex, Stack, type StackProps, Tag, Text } from '@tonic-one/react';
import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { CorrelatedRuleContent } from '../types';
import { useRiskAIExpressionsQuery } from '../query';

const RuleContent = (
  props: Omit<StackProps, 'content'> & {
    content: CorrelatedRuleContent[];
    onDelete?: (index: number) => void;
    Item?: (props: {
      children: ReactNode;
      onDelete: () => void;
    }) => JSX.Element;
  }
) => {
  const { content, onDelete, Item = Box, ...stackProps } = props;
  const { t } = useTranslation();
  const { data: expressions } = useRiskAIExpressionsQuery();

  const getExpression = useCallback(
    (id: string) => {
      return expressions?.find((e) => e.raiExpId === id);
    },
    [expressions]
  );

  return (
    <Stack {...stackProps}>
      {content.map((c, i) => {
        const statement = c?.statement || [];
        const itemProps: any = {
          key: `statement-${i}-expression`,
        };

        if (onDelete) {
          itemProps.onDelete = () => onDelete(i);
        }

        return (
          <Item {...itemProps}>
            <Flex gap="2x">
              {i !== 0 && (
                <Tag height="20px" width="max-content" size="sm">
                  {t('globalSetting.correlatedRules.preDefinedModal.or')}
                </Tag>
              )}
              <Flex>
                <Stack flexDirection="row" gap="2x" flexWrap="wrap">
                  {statement.map((s, j) => (
                    <Stack flexDirection="row" gap="2x" key={`statement-${j}`}>
                      <Text width="max-content">
                        {statement.length > 1 && j === 0 && '('}
                        {getExpression(s.raiExpId)?.raiExpName || ''}
                        {statement.length > 1 &&
                          j === statement.length - 1 &&
                          ')'}
                      </Text>
                      {j !== statement.length - 1 && (
                        <Tag width="max-content" size="sm">
                          {t(
                            'globalSetting.correlatedRules.preDefinedModal.and'
                          )}
                        </Tag>
                      )}
                    </Stack>
                  ))}
                </Stack>
              </Flex>
            </Flex>
          </Item>
        );
      })}
    </Stack>
  );
};

export default RuleContent;
